import React, { useMemo } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { formatCurrency } from "../../utils/formatCurrency";
import { TOOLBAR_DESCARGA, LEYENDA_DONA, SIN_LEYENDA } from "./graficos";
import { TITULO_CARD, TITULO_ACLARACION } from "./tituloCard";
// Las categorías de vehículo las nombra el cliente, así que no hay concepto que
// pedirle a ORIGEN: van con la paleta LIBRE, que es la misma gama de la
// pantalla nueva. La paleta además fija el color por NOMBRE y no por posición,
// que es lo que hacía que "Moto" cambiara de color al cambiar el período.
//
// Los tres bloques de este archivo arman la paleta con la lista COMPLETA de
// categorías —no con la que cada uno dibuja— para que "Moto" sea del mismo
// color en la dona, en las barras y en el resumen: si cada bloque repartiera
// sobre su propia lista, el que filtra las de monto cero repartiría distinto.
import { paletaLibre } from "../Stats2/theme";

const fmt = (v) => formatCurrency(v ?? 0, true);

/**
 * Aggregates category data from daily docs.
 * Returns null if no category data exists (backwards compatible).
 */
const useCategories = (docs) => {
  return useMemo(() => {
    const agg = {};
    let hasData = false;

    docs.forEach((d) => {
      if (!d.categories || typeof d.categories !== "object") return;
      hasData = true;
      Object.entries(d.categories).forEach(([name, data]) => {
        if (!agg[name]) agg[name] = { in: 0, out: 0, canceled: 0, amount: 0 };
        agg[name].in += data.in || 0;
        agg[name].out += data.out || 0;
        agg[name].canceled += data.canceled || 0;
        agg[name].amount += data.amount || 0;
      });
    });

    if (!hasData) return null;

    // Sort by entries descending, include categories with amounts even if 0 entries
    const sorted = Object.entries(agg)
      .filter(([, d]) => d.in > 0 || d.out > 0 || d.amount > 0)
      .sort((a, b) => (b[1].in || b[1].amount) - (a[1].in || a[1].amount));

    return sorted;
  }, [docs]);
};

// ── Donut: entries by category ──────────────────────────────
const CategoryDonut = ({ docs, heightOverride }) => {
  const categories = useCategories(docs);

  const { labels, series, colors, amountMode } = useMemo(() => {
    if (!categories) return { labels: [], series: [], colors: [], amountMode: false };
    const totalIn = categories.reduce((a, [, d]) => a + d.in, 0);
    const useAmount = totalIn === 0;
    const color = paletaLibre(categories.map(([nombre]) => nombre));
    return {
      labels: categories.map(([name, d]) => { const qty = d.in || d.out; return qty > 0 ? `${name} (${qty})` : name; }),
      series: categories.map(([, d]) => useAmount ? d.amount : d.in),
      // Mismo criterio que las barras: por nombre, para que la categoria
      // conserve su color entre la dona y el grafico de abajo.
      colors: categories.map(([nombre]) => color(nombre)),
      amountMode: useAmount,
    };
  }, [categories]);

  if (!categories || !series.length || series.every((v) => v === 0)) return null;

  const options = {
    chart: { type: "donut", fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
    labels,
    colors,
    // Los nombres de las categorías no están en ningún eje: sin leyenda, la
    // dona son colores sin nombre.
    legend: LEYENDA_DONA,
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
          labels: {
            show: true,
            value: { fontSize: "13px", fontWeight: 600, formatter: amountMode ? (v) => formatCurrency(Number(v), true) : undefined },
            total: { show: true, label: "Total", fontSize: "11px", formatter: amountMode ? (w) => formatCurrency(w.globals.seriesTotals.reduce((a, b) => a + b, 0), true) : undefined },
          },
        },
      },
    },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%`, style: { fontSize: "11px" } },
    tooltip: { y: { formatter: amountMode ? (v) => formatCurrency(v, true) : (v) => `${v} tickets` } },
  };

  return (
    <Card className="shadow-sm h-100">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>category</span>
          Qué tipo de vehículo entra más
          <span className="ms-1" style={TITULO_ACLARACION}>
            (entre paréntesis, la cantidad de tickets)
          </span>
        </h6>
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart options={options} series={series} type="donut" height={heightOverride || 280} />
      </CardBody>
    </Card>
  );
};

// ── Bar: income by category ─────────────────────────────────
const CategoryIncome = ({ docs, heightOverride }) => {
  const categories = useCategories(docs);

  const data = useMemo(() => {
    if (!categories) return null;
    const filtered = categories.filter(([, d]) => d.amount > 0);
    if (!filtered.length) return null;
    return filtered;
  }, [categories]);

  const color = useMemo(
    () => paletaLibre((categories || []).map(([nombre]) => nombre)),
    [categories]
  );

  if (!data) return null;

  const options = {
    chart: { type: "bar", height: heightOverride || 260, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%", distributed: true } },
    // El color sale del NOMBRE de la categoria, no de su posicion. Con
    // `distributed: true` ApexCharts toma un color por indice: si cambia el
    // periodo y "Moto" pasa de tercera a segunda, cambia de color y el duenno
    // cree que esta mirando otra cosa.
    // Habia ademas un `plotOptions` duplicado en este mismo objeto —el
    // primero quedaba muerto—, que es lo que hacia saltar no-dupe-keys.
    colors: data.map(([nombre]) => color(nombre)),
    dataLabels: { enabled: false },
    xaxis: {
      categories: data.map(([name, d]) => { const qty = d.in || d.out; return qty > 0 ? `${name} (${qty})` : name; }),
      labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "10px" } },
    },
    yaxis: { labels: { style: { fontSize: "11px" } } },
    tooltip: { y: { formatter: (v) => formatCurrency(v, true) } },
    // Sin leyenda a propósito: es un gráfico horizontal y cada barra ya lleva
    // el nombre de su categoría escrito al lado, en el eje. Una leyenda
    // obligaría a ir y volver entre el cuadradito y la barra para leer lo
    // mismo dos veces.
    legend: SIN_LEYENDA,
    grid: { borderColor: "#f1f1f1" },
  };

  return (
    <Card className="shadow-sm h-100">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>attach_money</span>
          Cuánto deja cada tipo de vehículo
          <span className="ms-1" style={TITULO_ACLARACION}>
            (entre paréntesis, la cantidad de tickets)
          </span>
        </h6>
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart
          options={options}
          series={[{ name: "Lo que dejó", data: data.map(([, d]) => d.amount) }]}
          type="bar"
          height={heightOverride || 260}
        />
      </CardBody>
    </Card>
  );
};

// ── Summary row for Resumen tab ────────────────────────────
const CategorySummary = ({ docs }) => {
  const categories = useCategories(docs);
  const color = useMemo(
    () => paletaLibre((categories || []).map(([nombre]) => nombre)),
    [categories]
  );

  if (!categories || categories.length < 2) return null;

  const totalIn = categories.reduce((a, [, d]) => a + d.in, 0);
  const totalAmount = categories.reduce((a, [, d]) => a + d.amount, 0);
  const useAmountMode = totalIn === 0 && totalAmount > 0;

  return (
    <Card className="shadow-sm h-100">
      <CardBody className="py-2">
        <h6 className="mb-2" style={{ ...TITULO_CARD, fontSize: 13 }}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>category</span>
          Tus cinco categorías más fuertes
          <span className="ms-1" style={TITULO_ACLARACION}>
            (últimos 30 días)
          </span>
        </h6>
        <div className="d-flex flex-column gap-2">
          {categories.slice(0, 5).map(([name, data]) => {
            const pct = useAmountMode
              ? (totalAmount > 0 ? (data.amount / totalAmount) * 100 : 0)
              : (totalIn > 0 ? (data.in / totalIn) * 100 : 0);
            return (
              <div key={name}>
                <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: 12 }}>
                  <span className="d-flex align-items-center gap-1">
                    {/* Por nombre, igual que la dona y las barras: el puntito
                        de esta lista tiene que ser del mismo color que la
                        porción de la categoría en el gráfico de al lado. */}
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color(name), display: "inline-block" }} />
                    {name}{(data.in || data.out) > 0 && <span className="text-muted ms-1">({data.in || data.out})</span>}
                  </span>
                  <span className="text-muted">
                    {fmt(data.amount)} <span style={{ fontSize: 10 }}>({pct.toFixed(0)}%)</span>
                  </span>
                </div>
                <div className="progress" style={{ height: 4 }}>
                  <div
                    className="progress-bar"
                    style={{ width: `${pct}%`, backgroundColor: color(name) }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
};

export { CategoryDonut, CategoryIncome, CategorySummary, useCategories };
